// === PERSONENAUTO — static product config ===
// Single source for the per-product identity, fixed insured sums, codelist defaults, the
// security-class shortlist, and the per-section schema allowlists used by the calc-payload
// builder. Pulled out of PersonenAutoScreen so adjusting the product is a config edit, not a
// screen edit. Mirrors the rechtsbijstand/fields.ts shape (RBV_PRODUCT / RBV_PP_DEFAULTS).

/** Carrier / product identity: EDP Personenauto, maatschappij R021, branche 021. */
export const AUTO_PRODUCT = {
  carrierCode: 'R021',
  productName: 'EDP Personenauto',
  ppMyaand: 'R021',
  branchCode: '021',
  afdDefn: '021-R021-EDP Personenauto',
  incWijze: 'I',
  veMyaand: 'R021',
} as const;

/** Fixed PP-level defaults seeded onto a fresh calculation payload (applied only when absent). */
export const AUTO_PP_DEFAULTS = {
  PP_CDUUMND: 12,
  PP_HANDACC: 'N',
  PP_STPRV: 'N',
  PP_EKST: 0,
  PP_BMEXTRA: 0,
  PP_SPECLIM: 'N',
  PP_PPRC: 20,
} as const;

/** Fixed WA insured sums — 10353 bodily injury / 10354 material damage. */
export const AUTO_WA_INSURED_SUMS = {
  WA_VZBLETS: 7500000,
  WA_VZBMATS: 2500000,
} as const;

/** Personenauto product codelist constants (applied only when the field is absent). */
export const AUTO_CODELIST = {
  /** 10047: WA_BMTABCD - B/M tabelcode */
  WA_BMTABCD: 'PN23',
  /** 10048: PP_BMINSCH - B/M inschalingstrede (default trede) */
  PP_BMINSCH: 1,
  /** 10208: PP_IVBLETC - IVB Lettercode ('AF' = Personenauto met aanhangwagen/oplegger, per SAA request) */
  PP_IVBLETC: 'AF',
  /** 10261: OB_FINLEAS - Financiering/lease indicator ('G' = geen financiering/lease) */
  OB_FINLEAS: 'G',
} as const;

// OB_TNOKLAS (TNO/SCM beveiligingsklasse) — passenger-car-relevant subset of the ADNTSB list,
// manually selected by the applicant. Labels live in i18n (securityClassOptions.<code>).
export const SECURITY_CLASS_CODES = ['9', '1', '2', '3', 'A4', '17', '6', '7', '8', 'F4', 'F5', '13', '5'] as const;

// OB_AFMKM (jaarkilometrage): the engine rates this as REAL kilometers, not as the ANVA
// dropdown code — a code lands in the lowest band, under-pricing every >10.000 km customer
// (live band sweep 2026-07-17: 2 / 7500 / 10000 km price identical; 15000 / 20000 / 99999
// climb). The wire therefore carries the band's kilometer value in OB_AFMKM.
// Stored step data keeps the CODE in OB_AFMKM so the dropdown keeps matching.
//
// EXPERIMENT(OB_AFMKMCD): the dropdown code rides along on the wire in OB_AFMKMCD — a
// self-invented label, NOT in the default AFD codelist. The calc engine accepts and echoes
// it, but ANVA can only bind default labels to a record, so it stays informational-only on
// the polis side (confirmed 2026-07-17). Kept in as a personal attempt; removing every
// EXPERIMENT(OB_AFMKMCD) site is fine and does not touch the km rating fix itself.
export const AFMKM_CODE_TO_KM: Record<string, number> = {
  '00010': 7500,
  '00007': 10000,
  '00008': 15000,
  '00002': 20000,
  '00003': 99999, // onbeperkt
};

const AFMKM_KM_TO_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(AFMKM_CODE_TO_KM).map(([code, km]) => [String(km), code]),
);

const padAfmkm = (value: unknown): string => String(value ?? '').padStart(5, '0');

/** Wire value for OB_AFMKM: a dropdown code maps to its band kilometers; km pass through. */
export const afmkmWireKm = (value: unknown): number =>
  AFMKM_CODE_TO_KM[padAfmkm(value)] ?? (Number(value) || 0);

/**
 * Dropdown code for any OB_AFMKM form: a (possibly zero-stripped) code, an OB_AFMKMCD echo
 * or a wire/echoed km value. Undefined when the value maps to neither.
 */
export const afmkmCode = (value: unknown): string | undefined => {
  const padded = padAfmkm(value);
  if (AFMKM_CODE_TO_KM[padded] !== undefined) return padded;
  return AFMKM_KM_TO_CODE[String(Number(value))];
};

// Per-section schema allowlists (the auto.json product schema is additionalProperties:false).
// The calc-payload builder strips any field not present here before sending to the BFF.
export const AUTO_FIELD_ALLOWLIST = {
  VP: new Set<string>(['VP_GEBDAT', 'VP_HUISNR', 'VP_PCODE', 'VP_PLAATS', 'VP_STRAAT', 'VP_TOEVOEG']),
  BS: new Set<string>([
    'BS_ANAAM', 'BS_GEBDAT', 'BS_GESLACH', 'BS_HUISNR', 'BS_PCODE',
    'BS_PLAATS', 'BS_RELSRTC', 'BS_STRAAT', 'BS_TOEVOEG', 'BS_VOORL', 'BS_VOORV',
  ]),
  OB: new Set<string>([
    'OB_AANKDAT', 'OB_AANTKW', 'OB_ABS', 'OB_ACCELER', 'OB_ACTKLR1', 'OB_ADEUR',
    'OB_AFMCC', 'OB_AFMKM', 'OB_AFMWE', 'OB_AUTOTLX', 'OB_BOUWJR', 'OB_BOUWMND',
    'OB_BRANSTO', 'OB_BRNDST2', 'OB_BTWCD', 'OB_CARRESS', 'OB_CMPEXCL', 'OB_CMPINCL',
    'OB_DAGWRDE', 'OB_DATTOEL', 'OB_EERSTEI', 'OB_FINLEAS', 'OB_GEBRUIK', 'OB_IMPOBJ',
    'OB_KENTEKE', 'OB_MERK', 'OB_MLDCODE', 'OB_MODEL', 'OB_OBJSRT', 'OB_TNOKLAS',
    'OB_TURBO', 'OB_TYPE', 'OB_VWACA', 'OB_VWAAC', 'OB_ZITPLAA',
    'OB_AFMKMCD', // EXPERIMENT(OB_AFMKMCD): self-invented label — remove together with the experiment
  ]),
  VE: new Set<string>(['VE_MYAAND']),
  WA: new Set<string>([
    'MP', 'WA_BMCOMTR', 'WA_BMEXTRA', 'WA_BMNAAND', 'WA_BMPERC', 'WA_BMTABCD', 'WA_BTP',
    'WA_CODE', 'WA_ENTREF', 'WA_NCAAND', 'WA_NCPRC', 'WA_NTP', 'WA_PKRTVMP',
    'WA_PPRC', 'WA_PTSLTB', 'WA_SCHDVRY', 'WA_TASS', 'WA_TKRT', 'WA_TKRTBM',
    'WA_TPP', 'WA_TTOT', 'WA_TTPPRV', 'WA_TTSL', 'WA_TTSLBM', 'WA_TTSLTB',
    'WA_VOLGNUM', 'WA_VZBLETS', 'WA_VZBMATS', 'WA_WPREMBP',
  ]),
  VW: new Set<string>(['VW_VOLGNUM', 'VW_VRWNR', 'VW_VRWOM', 'VW_VRWVERS', 'VW_URL']),
  PH: new Set<string>([
    'MP', 'PH_BMNAAND', 'PH_BMPERC', 'PH_BTP', 'PH_CODE', 'PH_ENTREF',
    'PH_GEBIED', 'PH_NCAAND', 'PH_NCPRC', 'PH_NTP', 'PH_PKRTVMP', 'PH_PPRC',
    'PH_PTSLTB', 'PH_SCHDVRY', 'PH_TASS', 'PH_TKRT', 'PH_TKRTBM', 'PH_TPKRTVM',
    'PH_TPP', 'PH_TTOT', 'PH_TTPPRV', 'PH_TTSL', 'PH_TTSLBM', 'PH_TTSLTB',
    'PH_VOLGNUM', 'PH_WPREMBP',
  ]),
  RP: new Set<string>([
    'MP', 'RP_BMNAAND', 'RP_BMPERC', 'RP_BTP', 'RP_CODE', 'RP_ENTREF',
    'RP_NCAAND', 'RP_NCPRC', 'RP_NTP', 'RP_PKRTVMP', 'RP_PPRC', 'RP_PTSLTB',
    'RP_SCHDVRY', 'RP_TASS', 'RP_TKRT', 'RP_TKRTBM', 'RP_TPKRTVM', 'RP_TPP',
    'RP_TTOT', 'RP_TTPPRV', 'RP_TTSL', 'RP_TTSLBM', 'RP_TTSLTB',
    'RP_VOLGNUM', 'RP_WPREMBP',
  ]),
  SV: new Set<string>([
    'MP', 'SV_BMNAAND', 'SV_BMPERC', 'SV_BTP', 'SV_CODE', 'SV_ENTREF',
    'SV_NCAAND', 'SV_NCPRC', 'SV_NTP', 'SV_PKRTVMP', 'SV_PPRC', 'SV_PTSLTB',
    'SV_SCHDVRY', 'SV_TASS', 'SV_TKRT', 'SV_TKRTBM', 'SV_TPKRTVM', 'SV_TPP',
    'SV_TTOT', 'SV_TTPPRV', 'SV_TTSL', 'SV_TTSLBM', 'SV_TTSLTB', 'SV_VERZSOM',
    'SV_VOLGNUM', 'SV_WPREMBP',
  ]),
  PP: new Set<string>([
    'AA', 'BS', 'CA', 'CG', 'CL', 'EL', 'LC', 'LI', 'LP', 'OB', 'PH', 'QS', 'RI', 'RP', 'SV',
    'TP', 'VE', 'VL', 'VP', 'VW', 'WA',
    'PP_AFDDEFN', 'PP_AGNTNR', 'PP_BETTERM', 'PP_BMINSCH', 'PP_BMCOMTR', 'PP_BMEXTRA', 'PP_BMNAAND',
    'PP_BMPERC', 'PP_BRANCHE', 'PP_CDUUMND', 'PP_COLL', 'PP_EKST', 'PP_EXPDAT', 'PP_EXTERN',
    'PP_HANDACC', 'PP_HVVDAT', 'PP_IBAN', 'PP_INCWIJZ', 'PP_INGDAT', 'PP_INGDTW',
    'PP_IVBLETC', 'PP_MYAAND', 'PP_NCAAND', 'PP_NCPRC', 'PP_NTP', 'PP_PKRTVMP', 'PP_PKST', 'PP_PPRC',
    'PP_PRODNM', 'PP_PTSLTB', 'PP_SCHDVRY', 'PP_SPECLIM', 'PP_SPLIMNR', 'PP_STATUS', 'PP_STPRV',
    'PP_TARDAT', 'PP_TASS', 'PP_TKRTBM', 'PP_TPP', 'PP_TTOT', 'PP_TTPPRV',
    'PP_TTSLBM', 'PP_TTSLTB', 'PP_PVADAT',
  ]),
};

export const AUTO_DOWNSTREAM_OB_ALLOWLIST = new Set<string>(
  [...AUTO_FIELD_ALLOWLIST.OB].filter((k) => k !== 'OB_AANKDAT'),
);

export const LI_KENTEKENHOUDER_PERSONAL = [
  'LI_ANAAM', 'LI_VOORL', 'LI_VOORV', 'LI_GESLACH', 'LI_GEBDAT',
  'LI_HUISNR', 'LI_TOEVOEG', 'LI_PCODE', 'LI_PLAATS', 'LI_STRAAT',
] as const;
